import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, AlertCircle, CheckCircle, Lock } from 'lucide-react';

/**
 * Step 2 — card input form. Stateless: parent owns the values + errors and
 * passes change/submit handlers down.
 */
export default function CardForm({
  card,
  errors,
  processing,
  orderIdShort,
  amount,
  onChange,
  onFillTestCard,
  onSubmit,
}) {
  const inputClass = (errKey) =>
    `h-12 text-base ${errors[errKey] ? 'border-red-500' : ''}`;

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="bg-green-50 border border-green-200 p-4 rounded-2xl flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <p className="text-sm text-green-900">
          <strong>Payment Initiated</strong>
          {orderIdShort && <> - Order ID: {orderIdShort}...</>}
        </p>
      </div>

      {/* Test card helper */}
      <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl">
        <div className="flex items-start gap-2 mb-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-amber-900">TEST ENVIRONMENT</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Use test card: 4444 5555 6666 7777 | CVV: 555 | Expiry: 01/35
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={onFillTestCard}
          variant="outline"
          size="sm"
          className="w-full text-xs h-8 border-amber-300 hover:bg-amber-100"
        >
          Fill Test Card
        </Button>
      </div>

      {/* Cardholder name */}
      <div>
        <Label htmlFor="cardholder-name" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
          Cardholder Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="cardholder-name"
          type="text"
          value={card.cardholder_name}
          onChange={(e) => onChange('cardholder_name', e.target.value)}
          placeholder="JOHN DOE"
          className={inputClass('cardholder_name')}
          disabled={processing}
          data-testid="cardholder-name-input"
        />
        {errors.cardholder_name && (
          <p className="text-xs text-red-500 mt-1">{errors.cardholder_name}</p>
        )}
      </div>

      {/* Card number */}
      <div>
        <Label htmlFor="card-number" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
          Card Number <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <Input
            id="card-number"
            type="text"
            value={card.card_number}
            onChange={(e) => onChange('card_number', e.target.value)}
            placeholder="4444 5555 6666 7777"
            className={`${inputClass('card_number')} pl-12`}
            disabled={processing}
            data-testid="card-number-input"
          />
          <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        </div>
        {errors.card_number && (
          <p className="text-xs text-red-500 mt-1">{errors.card_number}</p>
        )}
      </div>

      {/* Expiry month + year */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="expiry-month" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Expiry Month <span className="text-red-500">*</span>
          </Label>
          <Input
            id="expiry-month"
            type="text"
            value={card.expiry_month}
            onChange={(e) => onChange('expiry_month', e.target.value)}
            placeholder="MM"
            maxLength={2}
            className={inputClass('expiry')}
            disabled={processing}
            data-testid="expiry-month-input"
          />
        </div>
        <div>
          <Label htmlFor="expiry-year" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Expiry Year <span className="text-red-500">*</span>
          </Label>
          <Input
            id="expiry-year"
            type="text"
            value={card.expiry_year}
            onChange={(e) => onChange('expiry_year', e.target.value)}
            placeholder="YYYY"
            maxLength={4}
            className={inputClass('expiry')}
            disabled={processing}
            data-testid="expiry-year-input"
          />
        </div>
      </div>
      {errors.expiry && (
        <p className="text-xs text-red-500 -mt-3">{errors.expiry}</p>
      )}

      {/* CVV */}
      <div>
        <Label htmlFor="cvv" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
          CVV <span className="text-red-500">*</span>
        </Label>
        <Input
          id="cvv"
          type="text"
          value={card.cvv}
          onChange={(e) => onChange('cvv', e.target.value)}
          placeholder="555"
          maxLength={4}
          className={inputClass('cvv')}
          disabled={processing}
          data-testid="cvv-input"
        />
        {errors.cvv && (
          <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">3 or 4 digits on the back of your card</p>
      </div>

      <div className="bg-muted/30 border border-border p-3 rounded-xl">
        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          <strong className="text-foreground">No Refund Policy:</strong> All sales are final. We do not offer refunds, exchanges, or cancellations once payment has been processed.
        </p>
      </div>

      <Button
        type="submit"
        disabled={processing}
        className="btn-primary w-full h-14 text-lg font-black"
        data-testid="process-payment-button"
      >
        {processing ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin">⏳</span>
            Processing Payment...
          </span>
        ) : (
          `Pay $${amount.toFixed(2)} BND`
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Lock className="w-3 h-3" />
        <span>256-bit SSL encrypted payment</span>
      </div>
    </form>
  );
}
